import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import React, { memo, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  Drawer,
  Icon,
  IconButton,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography
} from 'investira.react.components';
import DatePicker from '../DatePicker';
import { useMRTFiltersContext } from '../../../contexts/MRTFiltersContext';

const getColumnId = column => column?.id || column?.accessorKey || String(column?.header || '');

const extractNodeText = node => {
  if (node === null || node === undefined || typeof node === 'boolean') {
    return '';
  }

  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(extractNodeText).join(' ');
  }

  if (React.isValidElement(node)) {
    return extractNodeText(node.props?.children);
  }

  return '';
};

const getColumnLabel = column => {
  if (typeof column?.header === 'string') {
    return column.header;
  }

  const headerText = extractNodeText(column?.header).replace(/\s+/g, ' ').trim();

  if (headerText) {
    return headerText;
  }

  return getColumnId(column);
};

/**
 * Renderiza o componente MRTFilters.
 *
 * @returns {JSX.Element|null} Elemento renderizado ou `null` quando não houver conteúdo para exibir.
 */
const MRTFilters = memo(() => {
  const filtersContext = useMRTFiltersContext();
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!filtersContext?.isOpen) {
      setErrors({});
    }
  }, [filtersContext?.isOpen]);

  if (!filtersContext?.isReady) {
    return null;
  }

  const handleClose = () => {
    setErrors({});
    filtersContext.closeDrawer();
  };

  const handleApply = () => {
    const result = filtersContext.applyDraft();

    if (result?.success) {
      setErrors({});
      return;
    }

    setErrors(result?.errors || {});
  };

  const handleClear = () => {
    setErrors({});
    filtersContext.clearDraft();
  };

  const renderSelectField = column => {
    const columnId = getColumnId(column);
    const options = filtersContext.filterOptions?.[columnId] || [];
    const draftValue = filtersContext.draftFilters?.[columnId] ?? '';

    return (
      <TextField
        select
        fullWidth
        size="small"
        variant="outlined"
        label={getColumnLabel(column)}
        value={draftValue}
        disabled={options.length === 0}
        onChange={event => {
          filtersContext.setDraftFilter(columnId, event.target.value);
        }}>
        <MenuItem value="">Todos</MenuItem>
        {options.map(option => (
          <MenuItem key={`${columnId}-${option.value}`} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    );
  };

  const renderMultiSelectField = column => {
    const columnId = getColumnId(column);
    const options = filtersContext.filterOptions?.[columnId] || [];
    const draftValue = Array.isArray(filtersContext.draftFilters?.[columnId])
      ? filtersContext.draftFilters[columnId]
      : [];
    const hasOptions = options.length > 0;

    return (
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" color="textPrimary">
            {getColumnLabel(column)}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="text"
              color="primary"
              disabled={!hasOptions}
              onClick={() => filtersContext.setDraftMultiSelectAll(columnId, options)}>
              Selecionar todos
            </Button>
            <Button
              size="small"
              variant="text"
              color="primary"
              disabled={!hasOptions || draftValue.length === 0}
              onClick={() => filtersContext.clearDraftMultiSelect(columnId)}>
              Limpar
            </Button>
          </Stack>
        </Stack>
        <Stack
          spacing={0.25}
          sx={{
            maxHeight: 220,
            overflowY: 'auto',
            border: theme => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 0.5
          }}>
          {hasOptions ? (
            options.map(option => {
              const checked = draftValue.includes(option.value);

              return (
                <Stack
                  key={`${columnId}-${option.value}`}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'background.main'
                    }
                  }}
                  onClick={() =>
                    filtersContext.toggleDraftMultiSelectValue(columnId, option.value)
                  }>
                  <Checkbox
                    size="small"
                    checked={checked}
                    onClick={event => event.stopPropagation()}
                    onChange={() =>
                      filtersContext.toggleDraftMultiSelectValue(columnId, option.value)
                    }
                  />
                  <ListItemText
                    primary={option.label}
                    primaryTypographyProps={{
                      variant: 'caption'
                    }}
                  />
                </Stack>
              );
            })
          ) : (
            <Typography variant="caption" color="textSecondary" sx={{ px: 1, py: 0.5 }}>
              Nenhuma opção disponível
            </Typography>
          )}
        </Stack>
      </Stack>
    );
  };

  const renderDateRangeField = column => {
    const columnId = getColumnId(column);
    const draftValue = filtersContext.draftFilters?.[columnId] || { month: '', year: '' };
    const pickerValue =
      draftValue.month && draftValue.year
        ? dayjs(`${draftValue.year}-${draftValue.month}-01`).locale('pt-br')
        : null;
    const helperText = errors[columnId] || ' ';

    return (
      <Stack spacing={1}>
        <Typography variant="body2" color="textPrimary">
          {getColumnLabel(column)}
        </Typography>
        <DatePicker
          label="Mês e Ano"
          format="MMMM YYYY"
          value={pickerValue}
          views={['month', 'year']}
          onChange={nextValue => {
            const parsedValue = dayjs.isDayjs(nextValue)
              ? nextValue.locale('pt-br')
              : dayjs(nextValue).locale('pt-br');

            if (!parsedValue?.isValid?.()) {
              filtersContext.setDraftFilter(columnId, { month: '', year: '' });
              return;
            }

            filtersContext.setDraftFilter(columnId, {
              month: parsedValue.format('MM'),
              year: parsedValue.format('YYYY')
            });
          }}
          slotProps={{
            layout: {
              sx: {
                '& .MuiPickersCalendarHeader-label': {
                  textTransform: 'capitalize'
                },
                '& .MuiPickersMonth-monthButton': {
                  width: '100%',
                  maxWidth: '100%',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  textTransform: 'capitalize'
                }
              }
            },
            textField: {
              fullWidth: true,
              size: 'small',
              variant: 'outlined',
              sx: {
                '& .MuiInputBase-input': {
                  textTransform: 'capitalize'
                }
              },
              error: Boolean(errors[columnId]),
              helperText
            }
          }}
        />
      </Stack>
    );
  };

  const renderFilterField = column => {
    if (column.filterVariant === 'date-range') {
      return renderDateRangeField(column);
    }

    if (column.filterVariant === 'multi-select') {
      return renderMultiSelectField(column);
    }

    return renderSelectField(column);
  };

  return (
    <Drawer anchor="right" open={filtersContext.isOpen} onClose={handleClose}>
      <Stack spacing={3} sx={{ width: { xs: '100vw', sm: 420 }, p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" color="textPrimary">
            Filtros
          </Typography>
          <IconButton onClick={handleClose} color="primary" size="small">
            <Icon iconName="cancel" size={18} />
          </IconButton>
        </Stack>
        <Divider />
        <Stack spacing={3}>
          {filtersContext.filterableColumns.map(column => (
            <Stack key={getColumnId(column)} spacing={1}>
              {renderFilterField(column)}
            </Stack>
          ))}
        </Stack>
        <Divider />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
          <Button variant="outlined" color="primary" onClick={handleClear}>
            Limpar
          </Button>
          {/* <Button variant="outlined" color="primary" onClick={handleClose}>
            Fechar
          </Button> */}
          <Button variant="contained" color="primary" onClick={handleApply}>
            Aplicar
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
});

export default MRTFilters;
